import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { apiClient } from "@/lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Settings as SettingsIcon,
  User,
  Lock,
  Palette,
  Bell,
  Shield,
  Database,
  Users,
  Building,
  Tag,
  Workflow,
  Info,
  Camera,
  Loader2,
  X,
} from "lucide-react";
import { cn, getErrorMessage } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { UserManagement } from "@/components/admin/UserManagement";
import { DepartmentManagement } from "@/components/admin/DepartmentManagement";
import { TicketTypeManagement } from "@/components/admin/TicketTypeManagement";
import { StageManagement } from "@/components/admin/StageManagement";

export function SettingsPage() {
  const { user, isAdmin, changePassword, refreshUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [profileName, setProfileName] = useState("");
  const [profilePosition, setProfilePosition] = useState("");
  const [profileAvatarUrl, setProfileAvatarUrl] = useState("");
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarUploadProgress, setAvatarUploadProgress] = useState(0);

  const MAX_AVATAR_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

  useEffect(() => {
    if (user?.profile) {
      setProfileName(user.profile.name ?? "");
      setProfilePosition(user.profile.position ?? "");
      setProfileAvatarUrl(user.profile.avatarUrl ?? "");
    }
  }, [user]);

  const handleProfileSave = async () => {
    setIsSavingProfile(true);
    try {
      await apiClient.updateProfile({
        name: profileName.trim() || undefined,
        position: profilePosition.trim() || undefined,
        avatarUrl: profileAvatarUrl.trim() || undefined,
      });
      await refreshUser();
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update profile"));
    } finally {
      setIsSavingProfile(false);
    }
  };

  const uploadAvatarToStorage = async (file: File): Promise<string> => {
    const res = await apiClient.prepareUpload({
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    });
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", res.uploadUrl, true);
      Object.entries(res.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable)
          setAvatarUploadProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve(res.attachmentId);
        else reject(new Error("Upload failed"));
      };
      xhr.onerror = () => reject(new Error("Upload failed"));
      xhr.send(file);
    });
  };

  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error("Please choose a JPEG, PNG, WebP or GIF image.");
      return;
    }
    if (file.size > MAX_AVATAR_SIZE) {
      toast.error("Image must be 5MB or smaller.");
      return;
    }
    setIsUploadingAvatar(true);
    setAvatarUploadProgress(0);
    try {
      const attachmentId = await uploadAvatarToStorage(file);
      const verified = await apiClient.verifyUpload(attachmentId);
      if (!verified) throw new Error("Verification failed");
      const downloadUrl = await apiClient.getDownloadUrl(attachmentId);
      await apiClient.updateProfile({ avatarUrl: downloadUrl });
      setProfileAvatarUrl(downloadUrl);
      await refreshUser();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to upload profile photo"));
    } finally {
      setIsUploadingAvatar(false);
      setAvatarUploadProgress(0);
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      await apiClient.updateProfile({ avatarUrl: "" });
      setProfileAvatarUrl("");
      await refreshUser();
      toast.success("Profile photo removed");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to remove photo"));
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    try {
      await changePassword(user?.email ?? "", newPassword, confirmPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      // Error is already handled by the auth hook
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <SettingsIcon className="h-8 w-8" />
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground">
            Manage your account and system preferences
          </p>
        </div>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className={cn(
          "grid w-full",
          isAdmin ? "grid-cols-2 lg:grid-cols-6" : "grid-cols-2"
        )}>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          {isAdmin && (
            <>
              <TabsTrigger value="system">System</TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="departments">Config</TabsTrigger>
              <TabsTrigger value="about">About</TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Profile Information
              </CardTitle>
              <CardDescription>
                View and manage your profile information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    placeholder="Your display name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={user?.email || ""} disabled className="mt-1" />
                </div>
                <div>
                  <Label>Position</Label>
                  <Input
                    value={profilePosition}
                    onChange={(e) => setProfilePosition(e.target.value)}
                    placeholder="e.g. Support Lead"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Profile photo</Label>
                  <div className="mt-2 flex items-center gap-4">
                    <Avatar className="h-20 w-20">
                      <AvatarImage
                        src={profileAvatarUrl || (user?.profile as { avatarUrl?: string })?.avatarUrl || undefined}
                        alt="Profile"
                      />
                      <AvatarFallback className="text-lg">
                        {profileName?.slice(0, 2)?.toUpperCase() || user?.email?.slice(0, 2)?.toUpperCase() || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <input
                          id="avatar-upload"
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          className="hidden"
                          onChange={handleAvatarFileChange}
                          disabled={isUploadingAvatar}
                        />
                        <Label
                          htmlFor="avatar-upload"
                          className={cn(
                            "cursor-pointer flex items-center gap-2 px-3 py-2 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground text-sm transition-colors",
                            isUploadingAvatar && "pointer-events-none opacity-70"
                          )}
                        >
                          {isUploadingAvatar ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Camera className="h-4 w-4" />
                          )}
                          {isUploadingAvatar ? "Uploading..." : "Upload photo"}
                        </Label>
                        {profileAvatarUrl && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={handleRemoveAvatar}
                            disabled={isUploadingAvatar}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Remove
                          </Button>
                        )}
                      </div>
                      {isUploadingAvatar && (
                        <Progress value={avatarUploadProgress} className="h-1.5 w-40" />
                      )}
                      <p className="text-xs text-muted-foreground">
                        JPEG, PNG, WebP or GIF. Max 5MB.
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Roles</Label>
                  <div className="flex gap-2 mt-1">
                    {user?.roles?.map((role) => (
                      <Badge key={role} variant="secondary">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              <Button onClick={handleProfileSave} disabled={isSavingProfile}>
                {isSavingProfile ? "Saving..." : "Save profile"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Change Password
              </CardTitle>
              <CardDescription>
                Update your password to keep your account secure
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="current-password">Current Password</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1"
                />
              </div>
              <Button
                onClick={handlePasswordChange}
                disabled={!currentPassword || !newPassword || !confirmPassword}
              >
                Update Password
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <p>
                  <strong>Account Status:</strong> Active
                </p>
                <p>
                  <strong>Two-Factor Authentication:</strong> Not configured
                </p>
                <p>
                  <strong>Last Login:</strong> Current session
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Tab */}
        <TabsContent value="system" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                System Settings
              </CardTitle>
              <CardDescription>
                Configure system-wide settings and preferences
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  System configuration features will be available in a future
                  update. This includes email settings, notification
                  preferences, and system limits.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                User Management
              </CardTitle>
              <CardDescription>
                Manage system users and their permissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <UserManagement />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="departments" className="space-y-6">
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Departments
                </CardTitle>
                <CardDescription>
                  Manage organizational departments
                </CardDescription>
              </CardHeader>
              <CardContent>
                <DepartmentManagement />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="h-5 w-5" />
                  Ticket Types
                </CardTitle>
                <CardDescription>
                  Configure available ticket types
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TicketTypeManagement />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Workflow className="h-5 w-5" />
                  Workflow Stages
                </CardTitle>
                <CardDescription>
                  Configure ticket workflow stages (Kanban columns)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <StageManagement />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* About Tab */}
        <TabsContent value="about" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                About Tickettrax
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold">Version</h4>
                  <p className="text-muted-foreground">1.0.0</p>
                </div>
                <div>
                  <h4 className="font-semibold">Backend API</h4>
                  <p className="text-muted-foreground">Express.js + Prisma</p>
                </div>
                <div>
                  <h4 className="font-semibold">Database</h4>
                  <p className="text-muted-foreground">PostgreSQL</p>
                </div>
              </div>

              <div className="pt-4 border-t">
                <h4 className="font-semibold mb-2">Features</h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>• Public ticket submission and tracking</li>
                  <li>• Role-based access control</li>
                  <li>• Kanban-style ticket management</li>
                  <li>• Real-time updates and notifications</li>
                  <li>• File attachment support</li>
                  <li>• Comprehensive dashboard and analytics</li>
                </ul>
              </div>

              <div className="pt-4 border-t">
                <h4 className="font-semibold mb-2">Support</h4>
                <p className="text-sm text-muted-foreground">
                  For technical support or questions, please contact your system
                  administrator or check the API documentation at{" "}
                  <a
                    href="http://localhost:3000/api"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    http://localhost:3000/api
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
